import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import useResource from '../../../../hooks/useResource';

function collapseConsecutiveResources(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return [];
  const out = [];
  for (const seg of segments) {
    const last = out.length > 0 ? out[out.length - 1] : null;
    if (last && last.rid === seg.rid) {
      const lastStartMs = last.start ? last.start.valueOf() : null;
      const lastEndMs = last.end ? last.end.valueOf() : null;
      const segStartMs = seg.start ? seg.start.valueOf() : null;
      const segEndMs = seg.end ? seg.end.valueOf() : null;

      const mergedStart =
        lastStartMs != null && segStartMs != null
          ? dayjs(Math.min(lastStartMs, segStartMs))
          : last.start || seg.start || null;
      const mergedEnd =
        lastEndMs != null && segEndMs != null
          ? dayjs(Math.max(lastEndMs, segEndMs))
          : last.end || seg.end || null;

      out[out.length - 1] = {
        ...last,
        start: mergedStart,
        end: mergedEnd,
        durationMinutes:
          mergedStart && mergedEnd ? Math.max(0, Math.round((mergedEnd.valueOf() - mergedStart.valueOf()) / 60000)) : last.durationMinutes,
        participantCount: last.participantCount ?? seg.participantCount ?? null,
      };
      continue;
    }
    out.push(seg);
  }
  return out;
}

function countDistinctSequence(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  let count = 0;
  let prev = null;
  for (const it of items) {
    if (it == null) continue;
    if (prev === null || String(it) !== String(prev)) {
      count += 1;
      prev = it;
    }
  }
  return count;
}

function OrderedResourceVisitPlan({ booking, open }) {
  const { getResourceById } = useResource();
  const getResourceByIdRef = useRef(getResourceById);
  const isBashBooking = String(booking?.entertainment || '') === 'BashZone';

  useEffect(() => {
    getResourceByIdRef.current = getResourceById;
  }, [getResourceById]);

  const [resourceNameById, setResourceNameById] = useState(() => new Map());

  useEffect(() => {
    if (!open) return undefined;
    if (!booking?._id) return undefined;
    if (!Array.isArray(booking?.orderedResourceIds) || booking.orderedResourceIds.length === 0) return undefined;

    const missingIds = new Set();
    booking.orderedResourceIds.forEach(seg => {
      const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
      if (!ridRaw) return;
      if (seg?.resourceId && typeof seg.resourceId === 'object' && seg.resourceId?.name) return;
      const rid = String(ridRaw);
      if (resourceNameById instanceof Map && resourceNameById.has(rid)) return;
      missingIds.add(rid);
    });

    if (missingIds.size === 0) return undefined;

    let canceled = false;
    const fetchMissing = async () => {
      const ids = Array.from(missingIds.values());
      const results = await Promise.allSettled(
        ids.map(async rid => {
          const res = await getResourceByIdRef.current(String(rid));
          const resource = res?.resource || res;
          const name = resource?.name ? String(resource.name) : null;
          return { rid: String(rid), name };
        })
      );

      if (canceled) return;

      setResourceNameById(prev => {
        const next = new Map(prev instanceof Map ? prev : []);
        results.forEach(r => {
          if (r.status !== 'fulfilled') return;
          const rid = r.value?.rid ? String(r.value.rid) : null;
          const name = r.value?.name ? String(r.value.name) : null;
          if (!rid || !name) return;
          next.set(rid, name);
        });
        return next;
      });
    };

    fetchMissing();
    return () => {
      canceled = true;
    };
  }, [open, booking?._id, booking?.orderedResourceIds, resourceNameById]);

  const normalizedSegments = useMemo(() => {
    try {
      if (!booking) return [];
      const segments = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
      if (segments.length === 0) return [];

      const baseStart = booking?.startTime ? dayjs(booking.startTime) : null;

      return segments
        .map((seg, idx) => {
          const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
          if (!ridRaw) return null;
          const rid = String(ridRaw);

          const nameFromSeg =
            seg?.resourceId && typeof seg.resourceId === 'object' && seg.resourceId?.name
              ? String(seg.resourceId.name)
              : null;
          const nameFromMap =
            resourceNameById instanceof Map ? resourceNameById.get(rid) : null;
          const name = nameFromSeg || nameFromMap || rid.substring(0, 6);

          const orderPositionRaw = Number(seg?.orderPosition);
          const orderPosition =
            Number.isFinite(orderPositionRaw) && orderPositionRaw > 0 ? orderPositionRaw : idx + 1;

          const startOffsetRaw = Number(seg?.startOffsetMinutes);
          const startOffsetMinutes = Number.isFinite(startOffsetRaw) ? startOffsetRaw : null;

          const durationRaw = Number(seg?.durationMinutes);
          const durationMinutes = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : null;

          const groupRaw = Number(seg?.groupIndex);
          const groupIndex = Number.isFinite(groupRaw) && groupRaw > 0 ? groupRaw : 1;

          const participantsRaw = Number(seg?.participantCount);
          const participantCount =
            Number.isFinite(participantsRaw) && participantsRaw > 0 ? participantsRaw : null;

          const start =
            baseStart && startOffsetMinutes != null ? baseStart.add(startOffsetMinutes, 'minute') : null;
          const end = start && durationMinutes != null ? start.add(durationMinutes, 'minute') : null;

          return {
            rid,
            name,
            orderPosition,
            startOffsetMinutes,
            durationMinutes,
            groupIndex,
            participantCount,
            start,
            end,
          };
        })
        .filter(Boolean);
    } catch (_) {
      return [];
    }
  }, [booking, resourceNameById]);

  const groups = useMemo(() => {
    if (normalizedSegments.length === 0) return [];

    const byGroup = new Map();
    normalizedSegments.forEach(seg => {
      const g = Number(seg.groupIndex || 1);
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(seg);
    });

    const groupEntries = Array.from(byGroup.entries()).map(([groupIndex, segs]) => {
      const list = Array.isArray(segs) ? segs.slice() : [];
      const hasAllOffsets = list.every(e => Number.isFinite(e.startOffsetMinutes));
      list.sort((a, b) => {
        if (isBashBooking) {
          if (a.orderPosition !== b.orderPosition) {
            return a.orderPosition - b.orderPosition;
          }
          const byName = String(a?.name || '').localeCompare(String(b?.name || ''), 'de', {
            numeric: true,
            sensitivity: 'base',
          });
          return byName;
        }
        if (hasAllOffsets) {
          return a.startOffsetMinutes - b.startOffsetMinutes;
        }
        return a.orderPosition - b.orderPosition;
      });

      const steps = collapseConsecutiveResources(list);
      const distinctInSequence = countDistinctSequence(steps.map(s => s.rid));
      const hasSwitch = distinctInSequence > 1;

      const sortKey = (() => {
        if (isBashBooking) {
          return Number(groupIndex);
        }
        const offsets = list
          .map(x => (Number.isFinite(x.startOffsetMinutes) ? Number(x.startOffsetMinutes) : null))
          .filter(v => v != null);
        if (offsets.length > 0) return Math.min(...offsets);
        return Number(groupIndex);
      })();

      return {
        groupIndex: Number(groupIndex),
        sortKey,
        steps,
        hasSwitch,
      };
    });

    groupEntries.sort((a, b) => a.sortKey - b.sortKey);
    return groupEntries;
  }, [normalizedSegments, isBashBooking]);

  const hasAnySwitch = useMemo(() => groups.some(g => g.hasSwitch), [groups]);
  const hasMultipleGroups = useMemo(() => groups.length > 1, [groups.length]);

  const summary = useMemo(() => {
    if (!hasAnySwitch) return '';
    return groups
      .filter(g => g.hasSwitch)
      .map(g => `Gruppe ${g.groupIndex}: ${g.steps.map(s => s.name).join(' \u2192 ')}`)
      .join(' | ');
  }, [groups, hasAnySwitch]);

  if (!booking || groups.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion
        disableGutters
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 1 } }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              Ablaufplan
            </Typography>
            {summary && (
              <Typography variant='caption' color='text.secondary'>
                {summary}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {groups.map(group => (
              <Box key={`ordered-visit-group-${booking?._id}-${group.groupIndex}`}>
                {hasMultipleGroups && (
                  <Typography variant='body2' sx={{ fontWeight: 700, mb: 0.5 }}>
                    Gruppe {group.groupIndex}
                  </Typography>
                )}
                <Stack spacing={1}>
                  {group.steps.map((seg, idx) => {
                    const timeText = (() => {
                      if (!seg?.start) return null;
                      const startText = seg.start.format('HH:mm');
                      const endText = seg?.end ? seg.end.format('HH:mm') : null;
                      if (endText) return `${startText} – ${endText}`;
                      return startText;
                    })();

                    const metaParts = [];
                    if (timeText) metaParts.push(timeText);
                    if (Number.isFinite(seg?.startOffsetMinutes)) {
                      const off = Number(seg.startOffsetMinutes);
                      metaParts.push(`${off >= 0 ? '+' : ''}${off} Min`);
                    }
                    if (Number.isFinite(seg?.durationMinutes)) {
                      metaParts.push(`${seg.durationMinutes} Min`);
                    }
                    if (Number.isFinite(seg?.participantCount) && seg.participantCount > 0) {
                      metaParts.push(`${seg.participantCount} Pers.`);
                    }

                    const showStepNumber = group.steps.length > 1;

                    return (
                      <Box
                        key={`ordered-visit-${booking?._id}-g${group.groupIndex}-${idx}-${seg.rid}`}
                        sx={{ display: 'flex', gap: 1 }}
                      >
                        {showStepNumber ? (
                          <Typography variant='body2' sx={{ minWidth: 22, fontWeight: 700 }}>
                            {idx + 1}.
                          </Typography>
                        ) : null}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {seg.name}
                          </Typography>
                          {metaParts.length > 0 && (
                            <Typography variant='caption' color='text.secondary'>
                              {metaParts.join(' | ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default OrderedResourceVisitPlan;

